import { useState } from 'react';
import { ArrowLeft, Key, CheckCircle } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { FormField } from '../ui/FormField';
import { Button } from '../ui/Button';
import type { PasswordResetFormData } from '../../types/auth';
import { authAutoComplete } from '../../utils/auth-autocomplete';

interface PasswordResetFormProps {
  onBack?: () => void;
  onSuccess?: () => void;
  email?: string;
  otpCode?: string;
}

export const PasswordResetForm: React.FC<PasswordResetFormProps> = ({
  onBack,
  onSuccess,
  email,
  otpCode,
}) => {
  const {
    isLoading,
    error,
    resetPassword,
    goToLogin,
    clearAuthError,
    validatePassword,
  } = useAuth();

  const [formData, setFormData] = useState<PasswordResetFormData>({
    newPassword: '',
    confirmPassword: '',
  });

  const [fieldErrors, setFieldErrors] = useState<{
    newPassword?: string;
    confirmPassword?: string;
  }>({});

  const [passwordResetSuccess, setPasswordResetSuccess] = useState(false);

  const validateForm = (): boolean => {
    const errors: typeof fieldErrors = {};

    const passwordError = validatePassword(formData.newPassword);
    if (passwordError) errors.newPassword = passwordError;

    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (formData.newPassword !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      const result = await resetPassword({
        email: email || '',
        otp: otpCode || '',
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword,
      });

      if (result.type === 'auth/resetPassword/fulfilled') {
        setPasswordResetSuccess(true);
      }
    } catch (error) {
      console.error('Password reset error:', error);
    }
  };

  const handleBack = () => {
    goToLogin();
    onBack?.();
  };

  const handleFieldChange = (field: keyof PasswordResetFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (fieldErrors[field as keyof typeof fieldErrors]) {
      setFieldErrors(prev => ({ ...prev, [field]: undefined }));
    }
    
    if (error) clearAuthError();
  };

  const handleSuccessAndGoToLogin = () => {
    onSuccess?.();
    goToLogin();
  };

  // Success state
  if (passwordResetSuccess) {
    return (
      <div className="w-full max-w-md mx-auto text-center">
        <div className="mb-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Password Reset Successful!
          </h1>
          <p className="text-gray-600">
            Your password has been successfully updated.
          </p>
        </div>

        <div className="mb-6 text-sm text-center text-gray-600">
          You can now sign in with your new password.
        </div>

        <Button
          onClick={handleSuccessAndGoToLogin}
          fullWidth
          size="lg"
        >
          Continue to Sign In
        </Button>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto">
      {/* Back Button */}
      <button
        onClick={handleBack}
        className="flex items-center text-gray-600 hover:text-gray-800 transition-colors mb-6"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Sign In
      </button>

      {/* Header */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Key className="w-8 h-8 text-blue-600" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Create New Password
        </h1>
        <p className="text-gray-600">
          Choose a strong password for your account
        </p>
        {email && (
          <p className="text-sm text-gray-500 mt-2">
            Account: <span className="font-medium">{email}</span>
          </p>
        )}
      </div>


      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <FormField
          label="New Password"
          name="newPassword"
          type="password"
          value={formData.newPassword}
          onChange={(value) => handleFieldChange('newPassword', value)}
          error={fieldErrors.newPassword}
          placeholder="Enter your new password"
          autoComplete={authAutoComplete('new-password')}
          required
        />

        <FormField
          label="Confirm New Password"
          name="confirmPassword"
          type="password"
          value={formData.confirmPassword}
          onChange={(value) => handleFieldChange('confirmPassword', value)}
          error={fieldErrors.confirmPassword}
          placeholder="Confirm your new password"
          autoComplete={authAutoComplete('new-password')}
          required
        />

        {/* Error Message - Plain Text Below Confirm Password Field */}
        {error && (
          <div className="text-sm" style={{ color: '#CA7373' }}>
            {error}
          </div>
        )}

        <Button
          type="submit"
          loading={isLoading}
          fullWidth
          size="lg"
        >
          Update Password
        </Button>
      </form>

      {/* Password Requirements */}
      <div className="mt-8 text-sm text-gray-600">
        <div className="font-medium mb-2 text-gray-700">Password Requirements:</div>
        <ul className="space-y-1">
          <li>• At least 8 characters long</li>
          <li>• Contains at least one uppercase letter</li>
          <li>• Contains at least one lowercase letter</li>
          <li>• Contains at least one number</li>
          <li>• Contains at least one special character</li>
        </ul>
      </div>
    </div>
  );
};